import { Fragment, type ReactNode } from 'react';
import { Navigate } from 'react-router';

import { type ZgriPermissionName } from '@/shared/session';

import { usePermissions } from '../hooks/use-permissions';

type Permission = ZgriPermissionName;

interface BaseProps {
  require: Permission | [Permission, ...Permission[]];
  match?: 'all' | 'any';
  children: ReactNode;
}

type VisibilityProps = BaseProps & {
  variant?: 'visibility';
  fallback?: ReactNode;
  redirectTo?: never;
};

type RedirectProps = BaseProps & {
  variant?: 'redirect';
  redirectTo: string;
  fallback?: never;
};

export type ZgriPermissionGuardProps = VisibilityProps | RedirectProps;

export function ZgriPermissionGuard({
  children,
  require,
  fallback,
  match = 'all',
  redirectTo,
  variant = 'visibility',
}: ZgriPermissionGuardProps) {
  const { canAll, canAny } = usePermissions();

  const requiredPermissions = Array.isArray(require) ? require : [require];
  const allowed = match === 'all' ? canAll(...requiredPermissions) : canAny(...requiredPermissions);

  if (allowed) {
    return children;
  }

  if (variant === 'visibility') {
    return <Fragment>{fallback ?? null}</Fragment>;
  }

  return <Navigate to={redirectTo ?? '/'} replace={true} />;
}
